import { cmsSettingsAccess } from "../../../helpers/cmsSettingsAccess";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { accountsPortal } from "../../../services/centralAuth";
import { Alert, Button, Form, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { domainError, SiteDomainList, SiteDomainService } from "../../../services/SiteDomainService";

const labels: Record<string, string> = {
  Preparing: "Preparing domain setup", WaitingForDns: "Waiting for your DNS records",
  SettingUpHttps: "Ownership verified — setting up HTTPS", WaitingForTrafficDns: "Ownership verified — waiting for the CNAME record",
  Live: "Your domain is live", Removing: "Disconnecting your domain", Removed: "Domain disconnected",
  Suspending: "Disconnecting an expired subscription", Suspended: "Own-domain subscription inactive",
  ValidationExpired: "The verification record has expired", RevalidationRequired: "Your domain needs verification again",
  Error: "Setup needs attention",
};

type Props = { publishedDomain?: string; siteId: string; onAvailabilityChange: (enabled: boolean) => void; onDomainChange: (domain: string) => void };

export default function CustomDomainSetup({ publishedDomain, siteId, onAvailabilityChange, onDomainChange }: Props) {
  const { canManageBilling, site } = cmsSettingsAccess(siteId);
  const service = useMemo(() => new SiteDomainService(), []);
  const [data, setData] = useState<SiteDomainList | null>(null);
  const [hostname, setHostname] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmRemoval, setConfirmRemoval] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [copied, setCopied] = useState("");
  const current = data?.Domains[0];
  const connectedDomain = data?.PublishedDomain?.trim() || publishedDomain?.trim();
  const subscriptionUrl = site?.resourceId && site.accountId
    ? `${accountsPortal}/subscriptions/${encodeURIComponent(site.resourceId)}?accountId=${encodeURIComponent(site.accountId)}`
    : "/account/details";

  const load = useCallback(async (signal?: AbortSignal) => {
    const next = await service.list(siteId, signal);
    if (signal?.aborted) return;
    setData(next);
    onAvailabilityChange(next.Enabled);
    if (next.Enabled) onDomainChange(next.PublishedDomain ?? "");
  }, [siteId, service, onAvailabilityChange, onDomainChange]);

  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    setData(null);
    setConfirmRemoval(false);
    const poll = async () => {
      try { await load(controller.signal); }
      catch (e) { if (!axios.isCancel(e) && !controller.signal.aborted) setError(domainError(e)); }
      if (!controller.signal.aborted) timer = setTimeout(poll, 15000);
    };
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [load]);

  const act = async (action: "create" | "check" | "renew-validation" | "delete") => {
    setBusy(true); setError(""); setNotice("");
    try {
      if (action === "create") await service.create(siteId, hostname);
      else if (current) await service.change(siteId, current.Id, action);
      setConfirmRemoval(false);
      setNotice(action === "delete" ? "Disconnection requested. Wait for it to finish before adding the domain again."
        : action === "check" ? "A new check has been requested. The status will update automatically."
        : action === "renew-validation" ? "A new verification record has been requested. Use the new value when it appears below."
        : "Domain setup started. Your DNS instructions will appear shortly.");
      await load();
    } catch (e) { setError(domainError(e)); }
    finally { setBusy(false); }
  };

  const copy = async (value: string, key: string) => {
    try { await navigator.clipboard.writeText(value); setCopied(key); }
    catch { setError("Copy was unavailable. Select and copy the record value from the table."); }
  };

  return <section className="app-list-section mt-4" aria-labelledby="custom-domain-title">
    <div className="app-list-section__header"><div>
      <h2 id="custom-domain-title" className="app-list-section__title">Own domain</h2>
      <p className="app-list-section__summary">Connect a domain you own, such as www.example.com.</p>
    </div></div>
    <div className="app-list-section__body">
      {error && <Alert variant="danger" role="alert">{error}</Alert>}
      {notice && <Alert variant="info" role="status">{notice}</Alert>}
      {!data && !error && <p role="status">Loading domain setup…</p>}
      {data && !data.Enabled && <>
        {connectedDomain ? <p>Current domain: <strong>{connectedDomain}</strong></p> : <p>No own domain is connected.</p>}
        <p>Online domain changes are currently unavailable. Contact support {connectedDomain ? "to change your connected domain" : "for help connecting your domain"}.</p>
      </>}
      {data && (!data.Enabled || (!current && !data.CanAdd)) && <>
        <p>Domain Hosting is the subscription add-on for using your own domain.</p>
        {canManageBilling
          ? <a className="btn btn-primary" href={subscriptionUrl}>{connectedDomain || data.CanAdd ? "Manage Domain Hosting add-on" : "Buy Domain Hosting add-on"}</a>
          : <p>Contact your billing account holder to manage the Domain Hosting add-on.</p>}
      </>}
      {data?.Enabled && !current && <>
        {!data.CanAdd && <Alert variant="info">An active subscription with the own-domain option is required. {canManageBilling ? <Link to="/account/details">View your subscription</Link> : "Contact your billing account holder."}</Alert>}
        <Form onSubmit={e => { e.preventDefault(); void act("create"); }}>
          <Form.Label htmlFor="custom-domain-hostname">Your hostname</Form.Label>
          <Form.Control id="custom-domain-hostname" type="text" value={hostname} onChange={e => setHostname(e.target.value)}
            placeholder="www.example.com" required maxLength={250} autoComplete="off" disabled={busy || !data.CanAdd} />
          <Form.Text>Use the www version of your domain. Root domains and other subdomains are not supported here yet.</Form.Text>
          <div className="mt-3"><Button type="submit" disabled={busy || !data.CanAdd || !hostname.trim()}>Connect domain</Button></div>
        </Form>
      </>}
      {data?.Enabled && current && <>
        <p><strong>{current.Hostname}</strong></p>
        <p role="status" aria-live="polite">{labels[current.State] ?? current.State}</p>
        {current.Error && <Alert variant="warning">{current.Error}</Alert>}
        {current.State === "Live" && <p><a href={`https://${current.Hostname}`} target="_blank" rel="noreferrer">Visit your site</a></p>}
        {current.State === "Suspended" && <p>{canManageBilling ? <><Link to="/account/details">Renew your own-domain subscription</Link> to reconnect this domain.</> : "Ask your billing account holder to renew the own-domain subscription to reconnect this domain."}</p>}
        {current.State !== "Removing" && current.DnsRecords.length > 0 && <>
          <p>Add these records at your DNS provider. Keep the TXT record after verification. DNS changes can take time to appear.</p>
          <Table responsive bordered size="sm"><thead><tr><th>Type</th><th>Name</th><th>Value</th><th>TTL</th><th>Copy</th></tr></thead>
            <tbody>{current.DnsRecords.map(record => <tr key={record.Type}>
              <td>{record.Type}</td><td><code>{record.FullyQualifiedName}</code></td><td style={{ overflowWrap: "anywhere" }}><code>{record.Value}</code></td><td>{record.Ttl}s</td>
              <td><Button type="button" size="sm" variant="outline-secondary" onClick={() => void copy(record.Value, record.Type)} aria-label={`Copy ${record.Type} value`}>{copied === record.Type ? "Copied" : "Copy value"}</Button></td>
            </tr>)}</tbody>
          </Table>
          <p className="text-muted">If your provider appends your domain automatically, omit that suffix from the Name. For example, use _dnsauth.www and www in the example.com zone.</p>
          {current.TokenExpiresUtc && !current.OwnershipVerified && <p>Verification record expires: {new Date(current.TokenExpiresUtc).toLocaleString()}.</p>}
        </>}
        {current.LastCheckedUtc && <p className="text-muted">Last checked: {new Date(current.LastCheckedUtc).toLocaleString()}. This page updates automatically.</p>}
        {current.State === "Removing" ? <p>Wait for disconnection to finish, then remove the DNS records if you no longer need them. You can re-add the domain here afterwards.</p> : <div className="d-flex gap-2 flex-wrap">
          <Button type="button" variant="outline-primary" disabled={busy} onClick={() => void act("check")}>Check again</Button>
          {!current.OwnershipVerified && <Button type="button" variant="outline-secondary" disabled={busy} onClick={() => void act("renew-validation")}>Get new verification record</Button>}
          <Button type="button" variant="outline-danger" disabled={busy} onClick={() => setConfirmRemoval(true)}>Disconnect domain</Button>
        </div>}
        {confirmRemoval && <Alert variant="warning" className="mt-3">
          <p>Disconnect {current.Hostname}? Visitors will no longer reach this site at that address. Your hosted site address will remain available.</p>
          <Button type="button" variant="danger" disabled={busy} onClick={() => void act("delete")}>Disconnect</Button>{" "}
          <Button type="button" variant="secondary" disabled={busy} onClick={() => setConfirmRemoval(false)}>Cancel</Button>
        </Alert>}
      </>}
    </div>
  </section>;
}
